"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { BadgeCheck, Building2, CircleCheck as CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurCredentials() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const quote = useRevealOnScroll<HTMLDivElement>();
  const footer = useRevealOnScroll<HTMLDivElement>();
  const certifications = [{
    name: "ISO 27001 Certified",
    issuer: "International Organization for Standardization",
    year: "2026",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/iso_cert_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/iso_cert_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/iso_cert_badge.png-wvc-sizes"
    },
    alt: "ISO 27001 security certification badge"
  }, {
    name: "SOC 2 Type II",
    issuer: "AICPA Security Standards",
    year: "2026",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/soc2_compliance_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/soc2_compliance_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/soc2_compliance_badge.png-wvc-sizes"
    },
    alt: "SOC 2 Type II compliance badge"
  }, {
    name: "Cyber Essentials Plus",
    issuer: "National Cyber Security Centre",
    year: "2026",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_essentials_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_essentials_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_essentials_badge.png-wvc-sizes"
    },
    alt: "Cyber Essentials Plus certification badge"
  }, {
    name: "GDPR Compliant",
    issuer: "Data Protection Authority",
    year: "2026",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/gdpr_compliant_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/gdpr_compliant_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/gdpr_compliant_badge.png-wvc-sizes"
    },
    alt: "GDPR compliance badge"
  }];
  const trustSignals = [{
    icon: Building2,
    label: "Enterprise Grade"
  }, {
    icon: ShieldCheck,
    label: "Fully Audited"
  }, {
    icon: BadgeCheck,
    label: "Regulatory Compliant"
  }, {
    icon: Sparkles,
    label: "AI-Verified Security"
  }];
  return <section data-section-id="312" id="our-credentials" className="relative overflow-hidden bg-background py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/3 rounded-lg bg-primary/15 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-primary">
            Certified & Audited
            <Sparkles className="size-3.5" />
          </span>
          <h2 className="mt-5 font-default text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tighter text-foreground">
            Built on a foundation of{" "}
            <em className="font-default italic text-primary">uncompromising security</em>.
          </h2>
          <p className="mt-6 font-default text-base sm:text-lg leading-relaxed text-muted-foreground">
            Our security framework is validated by global standards. We maintain 
            rigorous compliance to ensure your digital assets remain protected 
            against the most sophisticated modern threats.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 transition-all duration-700 ease-out delay-150 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {certifications.map((cert, i) => <Card key={cert.name} data-index={i} className="group relative border border-border bg-card transition-all duration-300 hover:-translate-y-1 shadow-md">
              <CardContent className="flex flex-col items-center px-6 pt-8 pb-7 text-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-accent/60">
                  <img src={cert.image.src} data-wvc-srcset={cert.image.srcset} data-wvc-sizes={cert.image.sizes} alt={cert.alt} loading="lazy" decoding="async" className="h-20 w-20 object-contain" />
                </div>

                <h3 className="mt-6 font-default text-lg font-semibold leading-snug text-foreground">
                  {cert.name}
                </h3>

                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  {cert.issuer}
                </p>

                <Badge variant="outline" className="mt-5 gap-1.5 border-primary/30 bg-primary/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-primary">
                  <CheckCircle2 className="size-3" />
                  Verified {cert.year}
                </Badge>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div ref={quote.ref} className={`mt-20 transition-all duration-700 ease-out delay-200 ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Card className="relative overflow-hidden border-0 bg-primary shadow-xl">
            <CardContent className="relative px-8 py-12 sm:px-14 sm:py-16">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-primary-foreground/80">
                  A Note from Our Lead Architect
                </span>
                <blockquote className="mt-6 font-default text-2xl sm:text-3xl lg:text-4xl font-medium leading-[1.25] tracking-tight text-primary-foreground">
                  "Security is not a static checkbox; it is a continuous, 
                  AI-driven evolution. We hold these certifications to prove 
                  that our infrastructure is as resilient as the businesses 
                  we protect."
                </blockquote>

                <div className="mt-8 flex items-center justify-center gap-3">
                  <SafeHydrate><Avatar className="size-12 ring-2 ring-primary-foreground/30">
                    <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/security_lead_avatar.png" alt="Lead Security Architect" />
                    <AvatarFallback className="bg-primary-foreground text-primary font-default">
                      IS
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div className="text-left">
                    <p className="font-default text-base font-semibold text-primary-foreground">
                      Alex Rivers
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-primary-foreground/70">
                      Lead Security Architect, InfinitySecure
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={footer.ref} className={`mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 transition-all duration-700 ease-out delay-300 ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {trustSignals.map((signal, i) => {
          const Icon = signal.icon;
          return <div key={signal.label} data-index={i} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
                <Icon className="size-3.5 text-primary" />
                <span>{signal.label}</span>
              </div>;
        })}
        </div>
      </div>
    </section>;
}